/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */
import { createAction } from '@reduxjs/toolkit';
import { WorkOrder } from 'common/types';
import { DASHBOARD_SCOPE, ScheduledDay, SECTION_ID } from './types';
import { createDashboardSlice } from './createDashboardSlice';
import {
    fetchContractorCalendar,
    fetchContractorDashboardClient,
} from './dashboardThunks';

interface ContractorDashboardExtraState {
    selectedClientId?: string;
    clients: {
        loading: boolean;
        error: any | null;
        data: {
            id: string;
            name: string;
            doingBusinessAs: string;
        }[];
    };
    contractorCalendar: {
        loading: boolean;
        error: any | null;
        data: {
            enabled: boolean;
            synced: boolean;
        };
    };
}

export const setSelectedClientId = createAction<string>('setSelectedClientId');

export const contractorDashboardSlice =
    createDashboardSlice<ContractorDashboardExtraState>({
        name: DASHBOARD_SCOPE.CONTRACTOR,
        initialState: {
            selectedInterval: '7 days',
            selectedClientId: undefined,
            clients: {
                loading: false,
                error: null,
                data: [],
            },
            sections: {
                [SECTION_ID.contractorCoverageArea]: {
                    loading: false,
                    error: null,
                    data: { serviceRadius: null },
                },
                [SECTION_ID.contractorTeamMembers]: {
                    loading: false,
                    error: null,
                    data: [],
                },
                [SECTION_ID.scheduledWorkOrders]: {
                    loading: false,
                    error: null,
                    data: {
                        scheduledDays: [] as ScheduledDay[] | undefined,
                        upNextWorkOrders: [] as WorkOrder[] | undefined,
                    },
                    params: {},
                },
                [SECTION_ID.needAttentionWorkOrders]: {
                    loading: false,
                    error: null,
                    data: {
                        workOrders: [] as WorkOrder[] | undefined,
                    },
                },
                [SECTION_ID.openOrders]: {
                    loading: false,
                    error: null,
                    data: undefined,
                },
                [SECTION_ID.orderDetails]: {
                    loading: false,
                    error: null,
                    data: undefined,
                },
                [SECTION_ID.monthlySpending]: {
                    loading: false,
                    error: null,
                    data: undefined,
                },
            },
            contractorCalendar: {
                loading: false,
                error: null,
                data: {
                    enabled: false,
                    synced: false,
                },
            },
        },
        extraReducers: builder => {
            builder
                .addCase(
                    fetchContractorDashboardClient.pending,
                    (state, action) => {
                        state.clients.loading = true;
                        state.clients.error = [];
                    }
                )
                .addCase(
                    fetchContractorDashboardClient.fulfilled,
                    (state, action) => {
                        state.clients.loading = false;
                        state.clients.error = null;
                        state.clients.data = action.payload;
                    }
                )
                .addCase(
                    fetchContractorDashboardClient.rejected,
                    (state, action) => {
                        state.clients.loading = false;
                        state.clients.data = [];
                    }
                )
                .addCase(fetchContractorCalendar.pending, (state, action) => {
                    state.contractorCalendar.loading = true;
                    state.contractorCalendar.error = null;
                })
                .addCase(fetchContractorCalendar.fulfilled, (state, action) => {
                    state.contractorCalendar.loading = false;
                    state.contractorCalendar.error = null;
                    state.contractorCalendar.data = action.payload;
                })
                .addCase(fetchContractorCalendar.rejected, (state, action) => {
                    state.contractorCalendar.loading = false;
                    state.contractorCalendar.data = {
                        enabled: false,
                        synced: false,
                    };
                })
                .addCase(setSelectedClientId, (state, action) => {
                    return {
                        ...state,
                        selectedClientId: action.payload,
                    };
                });
        },
    });
export const { reset, updateSectionParams } = contractorDashboardSlice.actions;

export default contractorDashboardSlice.reducer;
